import React, { useEffect, useMemo, useRef, useState } from "react";

// Single-file React app for Prompt Generator
// Uses Tailwind CSS for styling (loaded via CDN)
// Features:
// - Modular prompt builder with live preview
// - Presets (save/load/delete) via localStorage
// - Shareable URL (state encoded in the hash)
// - Copy to clipboard & download .txt
// - Lightweight token estimate & word count
// - Randomize tone/style; quick templates
// - Template picker with search & grouping
// - Mobile-friendly responsive layout

const TONES = [
  "Neutral", "Friendly", "Professional", "Confident", "Enthusiastic",
  "Instructive", "Empathetic", "Analytical", "Persuasive", "Playful",
];

const STYLES = [
  "Concise", "Detailed", "Step-by-step", "Narrative", "Formal",
  "Casual", "Technical", "Journalistic", "Academic", "Bullet-list",
];

const LANGS = [
  "Auto", "English", "Bahasa Indonesia", "Spanish", "French", "German",
  "Italian", "Portuguese", "Russian", "Japanese", "Korean", "Chinese",
  "Dutch", "Polish", "Arabic", "Hindi", "Turkish", "Swedish",
];

// Template Types
const TEMPLATE_TYPES = {
  BLOG: "Blog Post",
  NEWS: "News Article",
  MARKETING: "Marketing Copy",
  TECHNICAL: "Technical Content",
  BUSINESS: "Business Document"
};

// Frameworks
const FRAMEWORKS = [
  { key: "eeat", label: "E-E-A-T", desc: "Experience, Expertise, Authority, Trust" },
  { key: "aida", label: "AIDA", desc: "Attention, Interest, Desire, Action" },
  { key: "pas", label: "PAS", desc: "Problem, Agitate, Solve" },
  { key: "fab", label: "FAB", desc: "Features, Advantages, Benefits" },
  { key: "bab", label: "BAB", desc: "Before, After, Bridge" },
  { key: "4ps", label: "4Ps", desc: "Promise, Picture, Proof, Push" },
  { key: "quest", label: "QUEST", desc: "Qualify, Understand, Educate, Stimulate, Transition" },
  { key: "star", label: "STAR", desc: "Situation, Task, Action, Result" },
  { key: "inverted_pyramid", label: "Inverted Pyramid", desc: "Most important info first" },
  { key: "storytelling", label: "Storytelling", desc: "Narrative structure" },
  { key: "listicle", label: "Listicle", desc: "List-based article" },
  { key: "how_to", label: "How-To", desc: "Step-by-step guide" },
];

// SEO Optimizations
const SEO_OPTIMIZATIONS = [
  { key: "keyword_density", label: "Keyword Density", desc: "Optimize keyword usage (1-2%)" },
  { key: "lsi_keywords", label: "LSI Keywords", desc: "Include semantically related terms" },
  { key: "meta_tags", label: "Meta Tags", desc: "Title tag, meta description" },
  { key: "internal_linking", label: "Internal Linking", desc: "Suggest relevant internal links" },
  { key: "featured_snippet", label: "Featured Snippet", desc: "Optimize for position zero" },
  { key: "schema_markup", label: "Schema Markup", desc: "Add structured data suggestions" },
  { key: "readability", label: "Readability", desc: "Flesch score optimization" },
  { key: "user_intent", label: "User Intent", desc: "Match search intent" },
];

const TEMPLATES = [
  {
    key: "blog_seo_onpage",
    label: "Blog SEO Onpage",
    type: TEMPLATE_TYPES.BLOG,
    preset: {
      task: "Write an SEO-optimized blog post with comprehensive on-page optimization",
      role: "You are an expert SEO content strategist and copywriter specializing in on-page optimization",
      audience: "Target audience searching for this topic on Google",
      context: `Primary keyword: <your main keyword>\nSearch intent: <informational/transactional/navigational>\nCompetitor URLs: <top 3 competitor articles>\nTarget word count based on SERP analysis: <1500-2500 words>`,
      constraints: [
        "Create compelling H1 with primary keyword near the beginning",
        "Include keyword in first 100 words naturally",
        "Use H2/H3 hierarchy with semantic keywords",
        "Add meta title (50-60 chars) and description (150-160 chars)",
        "Include 5-7 internal linking opportunities",
        "Add image alt text suggestions with keywords",
        "Create FAQ section with schema markup",
        "Include a featured snippet optimized section",
        "Add CTA for user engagement",
        "Maintain keyword density of 1-2%",
        "Use LSI keywords throughout",
      ],
      tone: "Professional",
      style: "Detailed",
      length: 2000,
      output: "SEO-Optimized Markdown Article",
    },
  },
  {
    key: "news_post_authority",
    label: "News Post (Authority Style)",
    type: TEMPLATE_TYPES.NEWS,
    preset: {
      task: "Write a news article in the style of authoritative news outlets like CNN, Washington Post, or BBC",
      role: "You are a senior journalist at a major news publication with expertise in breaking news and investigative reporting",
      audience: "Informed readers seeking credible news coverage",
      context: `Headline news: <breaking story>\nKey facts: <who, what, when, where, why>\nSources: <official sources, eyewitnesses>\nRelated context: <background information>`,
      constraints: [
        "Lead with the most newsworthy element (inverted pyramid)",
        "Write a compelling headline under 70 characters",
        "Include dateline and byline",
        "Quote at least 2-3 authoritative sources",
        "Provide balanced perspective with multiple viewpoints",
        "Include relevant statistics and data",
        "Add context and background in later paragraphs",
        "Use AP Style or similar journalistic standards",
        "Fact-check all claims",
        "Include 'developing story' disclaimer if applicable",
        "Add related articles section",
      ],
      tone: "Neutral",
      style: "Journalistic",
      length: 800,
      output: "News Article",
    },
  },
  {
    key: "blog_long_form",
    label: "Long-Form Blog (Pillar Content)",
    type: TEMPLATE_TYPES.BLOG,
    preset: {
      task: "Create comprehensive pillar content that establishes topical authority",
      role: "You are a subject matter expert and content strategist creating authoritative long-form content",
      audience: "Readers seeking in-depth, comprehensive information",
      context: `Main topic: <topic>\nTarget keywords cluster: <primary and secondary keywords>\nContent gap analysis: <what competitors miss>`,
      constraints: [
        "Create 3000+ word comprehensive guide",
        "Include table of contents with jump links",
        "Add 10-15 H2 sections with H3 subsections",
        "Include data, statistics, and research citations",
        "Add custom graphics/infographic descriptions",
        "Create downloadable resource suggestions",
        "Include expert quotes and interviews",
        "Add case studies or examples",
        "Optimize for multiple related keywords",
        "Include comprehensive FAQ section",
      ],
      tone: "Authoritative",
      style: "Detailed",
      length: 3500,
      output: "Long-Form Pillar Article",
    },
  },
  {
    key: "news_breaking",
    label: "Breaking News (Live Update)",
    type: TEMPLATE_TYPES.NEWS,
    preset: {
      task: "Write breaking news coverage with live updates format",
      role: "You are a breaking news reporter providing real-time coverage",
      audience: "Readers seeking immediate news updates",
      context: `Breaking event: <event>\nTime: <timestamp>\nLocation: <where>\nInitial reports: <preliminary info>`,
      constraints: [
        "Start with BREAKING or URGENT tag",
        "Lead with newest information",
        "Include timestamp for each update",
        "Use bullet points for quick facts",
        "Mark unconfirmed reports clearly",
        "Include live quotes from scene",
        "Add 'This is a developing story' disclaimer",
        "Provide context without speculation",
        "Update headline as story develops",
      ],
      tone: "Urgent",
      style: "Concise",
      length: 500,
      output: "Breaking News Update",
    },
  },
  {
    key: "blog_comparison",
    label: "Comparison Blog (VS Post)",
    type: TEMPLATE_TYPES.BLOG,
    preset: {
      task: "Create detailed comparison content between products/services/concepts",
      role: "You are an unbiased reviewer providing comprehensive comparisons",
      audience: "Readers making purchase or decision choices",
      context: `Item A: <first item>\nItem B: <second item>\nComparison criteria: <features, price, performance>`,
      constraints: [
        "Create comparison table/matrix",
        "Include pros and cons for each",
        "Add side-by-side specifications",
        "Provide use case scenarios",
        "Include pricing breakdown",
        "Add verdict for different user types",
        "Maintain neutral, unbiased tone",
        "Include user reviews/ratings",
        "Add FAQ about both options",
      ],
      tone: "Analytical",
      style: "Detailed",
      length: 1800,
      output: "Comparison Article",
    },
  },
  {
    key: "news_investigative",
    label: "Investigative Report",
    type: TEMPLATE_TYPES.NEWS,
    preset: {
      task: "Write in-depth investigative journalism piece",
      role: "You are an investigative journalist uncovering important stories",
      audience: "Readers interested in deep, thorough reporting",
      context: `Investigation topic: <topic>\nKey findings: <discoveries>\nSources: <documents, interviews>\nTimeline: <investigation period>`,
      constraints: [
        "Start with compelling lead revealing key finding",
        "Include methodology of investigation",
        "Present evidence systematically",
        "Include document references",
        "Add timeline of events",
        "Quote multiple sources",
        "Address counterarguments",
        "Include infographic/data visualization descriptions",
        "Add calls for accountability/action",
        "Provide source documentation",
      ],
      tone: "Serious",
      style: "Investigative",
      length: 2500,
      output: "Investigative Report",
    },
  },
  {
    key: "code_reviewer",
    label: "Code Reviewer",
    preset: {
      task: "Review the following code for correctness, security, and style.",
      role: "You are a senior software engineer and security reviewer",
      audience: "Developers",
      context: `Language: <language>\nCode snippet: \`\`\`\n<your code here>\n\`\`\``,
      constraints: [
        "Point out bugs and risky patterns",
        "Suggest clearer naming and structure",
        "Propose tests",
        "Keep feedback actionable and prioritized",
      ],
      tone: "Analytical",
      style: "Bullet-list",
      length: 400,
      output: "Review Notes",
    },
  },
  {
    key: "support_reply",
    label: "Customer Support Reply",
    preset: {
      task: "Draft a helpful support response",
      role: "You are a calm, empathetic customer support specialist",
      audience: "Non-technical customer",
      context: `Customer issue summary: <paste here>\nKnown constraints: <warranty, region, etc>`,
      constraints: [
        "Acknowledge feelings and apologize if needed",
        "Explain steps clearly",
        "Offer workaround and next actions",
        "Keep it short and scannable",
      ],
      tone: "Empathetic",
      style: "Concise",
      length: 180,
      output: "Email",
    },
  },
  {
    key: "image_prompt",
    label: "Image Prompt (Text→Image)",
    preset: {
      task: "Generate a vivid image prompt",
      role: "You are a creative art director",
      audience: "Text-to-image model",
      context: `Subject: <e.g., koi pond at night>\nStyle cues: <e.g., cinematic, long exposure>\nLens/Camera (optional): <e.g., 50mm, f/1.8>`,
      constraints: [
        "Describe subject, setting, lighting, mood, and composition",
        "Include colors and textures",
        "Avoid copyrighted characters",
      ],
      tone: "Enthusiastic",
      style: "Descriptive",
      length: 120,
      output: "Image Prompt",
    },
  },
  {
    key: "research_brief",
    label: "Research Brief",
    preset: {
      task: "Summarize and synthesize research on a topic",
      role: "You are a meticulous researcher",
      audience: "Busy executives",
      context: `Topic: <insert>\nSources: <links> (assess credibility)`,
      constraints: [
        "Provide 5-7 bullet insights",
        "Include caveats/limitations",
        "Give 2-3 action recommendations",
      ],
      tone: "Professional",
      style: "Bullet-list",
      length: 300,
      output: "Executive Brief",
    },
  },
  {
    key: "eeat_article",
    label: "Article (E-E-A-T)",
    preset: {
      task: "Write an authoritative article that demonstrates Experience, Expertise, Authoritativeness, and Trustworthiness",
      role: "You are an expert practitioner and careful journalist",
      audience: "Readers seeking reliable, up-to-date information",
      context: `Topic: <insert>\nAuthor: <name>, credentials: <degrees/certs/years>\nIf you cite sources, include publication date.`,
      constraints: [
        "Open with a concise summary and your first-hand experience",
        "Back claims with recent, reputable sources",
        "Add a Risks & Caveats section",
        "Include an 'About the author' box",
        "Add a Sources section with links",
        "Provide FAQ with 3-5 questions",
      ],
      tone: "Professional",
      style: "Detailed",
      length: 1200,
      output: "Markdown Article",
    },
  },
  {
    key: "pas_copy",
    label: "Copywriting (PAS)",
    preset: {
      task: "Write conversion copy using Problem-Agitate-Solve",
      role: "You are a direct-response copywriter",
      audience: "Potential buyers",
      context: `Product/Offer: <describe>\nAudience pain points: <list>\nProof assets: <testimonials, stats>`,
      constraints: [
        "Structure with clear headings: Problem, Agitate, Solve",
        "Use vivid, specific agitation",
        "Present solution with 3 benefit bullets",
        "Include 2 CTA variants",
        "Handle 3 common objections",
      ],
      tone: "Persuasive",
      style: "Concise",
      length: 500,
      output: "Landing Page Copy",
    },
  },
  {
    key: "aida_ad",
    label: "Ads/LP (AIDA)",
    preset: {
      task: "Create advertising/landing copy using AIDA",
      role: "You are a performance marketer",
      audience: "High-intent visitors",
      context: `Offer: <describe>\nChannel: <search/social/display>`,
      constraints: [
        "Sections: Attention, Interest, Desire, Action",
        "3 headline variants (max 30 chars each)",
        "2 primary descriptions (max 90 chars)",
        "List 3 benefits and 3 features",
        "Add 2 CTAs and 3 keyword ideas",
      ],
      tone: "Confident",
      style: "Bullet-list",
      length: 250,
      output: "Ad Set Copy",
    },
  },
  {
    key: "fab_product",
    label: "Product (F-A-B)",
    preset: {
      task: "Describe a product using Features-Advantages-Benefits",
      role: "You are a product marketer",
      audience: "Evaluators and buyers",
      context: `Product: <name>\nSpecs: <key specs>\nPersona: <describe>`,
      constraints: [
        "Provide a table: Feature | Advantage | Benefit",
        "Map benefits to persona pain points",
        "Add social proof snippet",
        "Finish with comparison vs. top alternative",
      ],
      tone: "Professional",
      style: "Bullet-list",
      length: 400,
      output: "Markdown with Table",
    },
  },
  {
    key: "bab_bridge",
    label: "Story (BAB)",
    preset: {
      task: "Write a Before-After-Bridge landing section",
      role: "You are a storytelling copywriter",
      audience: "Problem-aware visitors",
      context: `Current state (Before): <describe>\nDesired state (After): <describe>`,
      constraints: [
        "Section 1: Before (paint the pain)",
        "Section 2: After (paint the payoff)",
        "Section 3: Bridge (how the product gets them there)",
        "Include a visual metaphor suggestion",
      ],
      tone: "Persuasive",
      style: "Narrative",
      length: 250,
      output: "Landing Section",
    },
  },
  {
    key: "four_ps",
    label: "Copy (4Ps)",
    preset: {
      task: "Draft copy using Promise, Picture, Proof, Push",
      role: "You are a conversion copywriter",
      audience: "Busy decision-makers",
      context: `Offer: <describe>\nProof: <metrics, testimonials>`,
      constraints: [
        "Promise: compelling one-liner",
        "Picture: outcome vignette (2-3 sentences)",
        "Proof: concrete evidence",
        "Push: strong CTA + risk-reversal",
      ],
      tone: "Confident",
      style: "Concise",
      length: 220,
      output: "Sectioned Copy",
    },
  },
  {
    key: "quest_email",
    label: "Sales Email (QUEST)",
    preset: {
      task: "Write a cold/warm outreach email using QUEST",
      role: "You are an SDR writing tailored outreach",
      audience: "Prospects",
      context: `Persona: <role/industry>\nTrigger: <event/signal>\nValue prop: <one-liner>`,
      constraints: [
        "Qualify: relevance hook in 1 line",
        "Understand: reflect their situation",
        "Educate: 1-2 insights or stats",
        "Stimulate: outline transformation",
        "Transition: clear CTA with 2 slots",
      ],
      tone: "Professional",
      style: "Concise",
      length: 140,
      output: "Email",
    },
  },
  {
    key: "acca_explainer",
    label: "Explainer (ACCA)",
    preset: {
      task: "Write an explainer using Awareness-Comprehension-Conviction-Action",
      role: "You are an educator",
      audience: "Non-experts",
      context: `Topic: <insert>`,
      constraints: [
        "Awareness: why it matters",
        "Comprehension: simple model/analogy",
        "Conviction: evidence and counter-arguments",
        "Action: steps or checklist",
      ],
      tone: "Instructive",
      style: "Step-by-step",
      length: 600,
      output: "Guide",
    },
  },
  {
    key: "star_case",
    label: "Case Study (STAR)",
    preset: {
      task: "Write a case study using Situation-Task-Action-Result",
      role: "You are a consultant documenting impact",
      audience: "Executives",
      context: `Client: <name/industry>\nKPI: <metrics>`,
      constraints: [
        "Situation & Task (context + objective)",
        "Action (what was done; tools/process)",
        "Result (quantified outcomes)",
        "Lessons learned & next steps",
      ],
      tone: "Analytical",
      style: "Detailed",
      length: 700,
      output: "Case Study",
    },
  },
  {
    key: "product_review",
    label: "Product Review",
    preset: {
      task: "Write a product review emphasizing first-hand testing",
      role: "You are a reviewer who actually tested the product",
      audience: "Shoppers comparing options",
      context: `Product: <model>\nTest setup: <devices, duration, scenarios>`,
      constraints: [
        "Disclose how you tested it",
        "Pros/Cons table with weightings",
        "Compare against 2 alternatives",
        "Include maintenance/issues",
        "Conclude with verdict and score",
      ],
      tone: "Trustworthy",
      style: "Detailed",
      length: 900,
      output: "Markdown Review",
    },
  },
];

const DEFAULT_STATE = {
  task: "",
  role: "",
  audience: "",
  context: "",
  constraints: [""],
  tone: "Neutral",
  style: "Concise",
  depth: 3,
  length: 300,
  language: "Auto",
  output: "Plain Text",
  framework: "",
  optimizations: [],
  templateType: "",
};

// Template metadata for grouping and descriptions
const TEMPLATE_META = {
  blog_seo_onpage: { group: "Blog Posts", desc: "Full SEO on-page optimization" },
  blog_long_form: { group: "Blog Posts", desc: "3000+ word pillar content" },
  blog_comparison: { group: "Blog Posts", desc: "Product/service comparisons" },
  news_post_authority: { group: "News Articles", desc: "CNN/WaPo style reporting" },
  news_breaking: { group: "News Articles", desc: "Breaking news updates" },
  news_investigative: { group: "News Articles", desc: "In-depth investigation" },
  code_reviewer: { group: "Technical", desc: "Actionable code review notes" },
  support_reply: { group: "Communication", desc: "Empathetic support response" },
  image_prompt: { group: "Creative", desc: "Vivid text-to-image prompt" },
  research_brief: { group: "Business", desc: "Executive summary + actions" },
  eeat_article: { group: "SEO", desc: "Trust-focused article (E-E-A-T)" },
  pas_copy: { group: "Marketing", desc: "Problem-Agitate-Solve copy" },
  aida_ad: { group: "Marketing", desc: "Attention→Action ad flow" },
  fab_product: { group: "Marketing", desc: "Features-Advantages-Benefits" },
  bab_bridge: { group: "Marketing", desc: "Before-After-Bridge story" },
  four_ps: { group: "Marketing", desc: "Promise, Picture, Proof, Push" },
  quest_email: { group: "Sales", desc: "Sales email QUEST framework" },
  acca_explainer: { group: "Education", desc: "Awareness→Action explainer" },
  star_case: { group: "Business", desc: "Case study (STAR method)" },
  product_review: { group: "Reviews", desc: "First-hand product review" },
};

// Tone & Style descriptions
const TONE_META = {
  Neutral: "Balanced, unbiased",
  Friendly: "Warm, approachable",
  Professional: "Formal and polished",
  Confident: "Assertive, authoritative",
  Enthusiastic: "Energetic, passionate",
  Instructive: "Teaching, clear",
  Empathetic: "Understanding, caring",
  Analytical: "Data-driven, logical",
  Persuasive: "Convincing, compelling",
  Playful: "Fun, lighthearted",
};

const STYLE_META = {
  Concise: "Brief and to the point",
  Detailed: "Comprehensive, thorough",
  "Step-by-step": "Sequential instructions",
  Narrative: "Story-based approach",
  Formal: "Academic, official",
  Casual: "Conversational, relaxed",
  Technical: "Industry-specific terms",
  Journalistic: "News-style writing",
  Academic: "Scholarly format",
  "Bullet-list": "Quick points format",
};

// Utility functions
function serializeState(state) {
  try {
    const json = JSON.stringify(state);
    return btoa(unescape(encodeURIComponent(json)));
  } catch (e) {
    return "";
  }
}

function deserializeState(hash) {
  try {
    const json = decodeURIComponent(escape(atob(hash)));
    return JSON.parse(json);
  } catch (e) {
    return null;
  }
}

function useLocalState(initial) {
  const [state, setState] = useState(() => {
    const hash = window.location.hash.replace(/^#/, "");
    const parsed = deserializeState(hash);
    if (parsed) return { ...DEFAULT_STATE, ...parsed };
    const ls = localStorage.getItem("promptgen/state");
    return ls ? { ...DEFAULT_STATE, ...JSON.parse(ls) } : { ...DEFAULT_STATE, ...initial };
  });
  
  useEffect(() => {
    localStorage.setItem("promptgen/state", JSON.stringify(state));
    const encoded = serializeState(state);
    window.history.replaceState(null, "", "#" + encoded);
  }, [state]);
  
  return [state, setState];
}

function estimateTokens(text) {
  const words = (text.trim().match(/\S+/g) || []).length;
  const tokens = Math.round(words / 0.75);
  return { words, tokens };
}

// Component: Framework Selector
function FrameworkSelector({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const popRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState('bottom');

  useEffect(() => {
    if (open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      if (spaceBelow < 320 && spaceAbove > spaceBelow) {
        setDropdownPosition('top');
      } else {
        setDropdownPosition('bottom');
      }
    }
  }, [open]);

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") setOpen(false);
    }
    function onClick(e) {
      if (!open) return;
      if (
        popRef.current &&
        !popRef.current.contains(e.target) &&
        btnRef.current &&
        !btnRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    }
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  return (
    <div className="relative">
      <label className="text-sm opacity-80 mb-1 block">Content Framework</label>
      <button
        ref={btnRef}
        onClick={() => setOpen((v) => !v)}
        className="w-full px-3 py-2 rounded-xl border border-zinc-200 dark:border-zinc-700 text-left flex items-center justify-between hover:bg-zinc-100/60 dark:hover:bg-zinc-800/60 transition-colors"
      >
        <span className="truncate pr-2">
          {value ? FRAMEWORKS.find(f => f.key === value)?.label : "Select Framework (Optional)"}
        </span>
        <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
        </svg>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            ref={popRef}
            className={`absolute z-50 ${dropdownPosition === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'} w-full bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-2xl shadow-2xl overflow-hidden`}
            style={{ maxHeight: 'min(320px, 40vh)' }}
          >
            <div className="overflow-y-auto" style={{ maxHeight: 'min(300px, 35vh)' }}>
              <div className="p-2">
                <button
                  onClick={() => {
                    onChange("");
                    setOpen(false);
                  }}
                  className="w-full text-left p-3 rounded-xl border border-zinc-200 dark:border-zinc-800 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 mb-2"
                >
                  <div className="text-sm font-medium">No Framework</div>
                  <div className="text-xs text-zinc-500 mt-0.5">Use default template structure</div>
                </button>
                {FRAMEWORKS.map((framework) => (
                  <button
                    key={framework.key}
                    onClick={() => {
                      onChange(framework.key);
                      setOpen(false);
                    }}
                    className={`w-full text-left p-3 rounded-xl border ${
                      value === framework.key
                        ? "border-blue-400 dark:border-blue-600 bg-blue-50 dark:bg-blue-900/20"
                        : "border-zinc-200 dark:border-zinc-800"
                    } hover:border-zinc-300 dark:hover:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 mb-2 transition-all`}
                  >
                    <div className="text-sm font-medium">{framework.label}</div>
                    <div className="text-xs text-zinc-500 mt-0.5">{framework.desc}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Component: SEO Optimizations
function OptimizationsSelector({ value = [], onChange }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const popRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState('bottom');

  useEffect(() => {
    if (open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      if (spaceBelow < 400 && spaceAbove > spaceBelow) {
        setDropdownPosition('top');
      } else {
        setDropdownPosition('bottom');
      }
    }
  }, [open]);

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") setOpen(false);
    }
    function onClick(e) {
      if (!open) return;
      if (
        popRef.current &&
        !popRef.current.contains(e.target) &&
        btnRef.current &&
        !btnRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    }
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  const toggleOptimization = (key) => {
    const newValue = value.includes(key)
      ? value.filter((v) => v !== key)
      : [...value, key];
    onChange(newValue);
  };

  return (
    <div className="relative">
      <label className="text-sm opacity-80 mb-1 block">SEO Optimizations</label>
      <button
        ref={btnRef}
        onClick={() => setOpen((v) => !v)}
        className="w-full px-3 py-2 rounded-xl border border-zinc-200 dark:border-zinc-700 text-left flex items-center justify-between hover:bg-zinc-100/60 dark:hover:bg-zinc-800/60 transition-colors"
      >
        <span className="truncate pr-2">
          {value.length > 0 ? `${value.length} optimizations selected` : "Select optimizations (Optional)"}
        </span>
        <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
        </svg>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            ref={popRef}
            className={`absolute z-50 ${dropdownPosition === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'} w-full bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-2xl shadow-2xl overflow-hidden`}
            style={{ maxHeight: 'min(400px, 50vh)' }}
          >
            <div className="p-3 border-b border-zinc-200 dark:border-zinc-800 bg-zinc-50/70 dark:bg-zinc-950/40 sticky top-0">
              <div className="text-xs text-zinc-500">Check optimizations to include</div>
            </div>
            <div className="overflow-y-auto" style={{ maxHeight: 'min(300px, 40vh)' }}>
              <div className="p-2">
                {SEO_OPTIMIZATIONS.map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => toggleOptimization(opt.key)}
                    className="w-full text-left p-3 rounded-xl border border-zinc-200 dark:border-zinc-800 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 mb-2 flex items-start gap-3"
                  >
                    <input
                      type="checkbox"
                      checked={value.includes(opt.key)}
                      onChange={() => {}}
                      className="mt-0.5 accent-blue-500"
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium">{opt.label}</div>
                      <div className="text-xs text-zinc-500 mt-0.5">{opt.desc}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
            <div className="p-3 border-t border-zinc-200 dark:border-zinc-800 bg-zinc-50/70 dark:bg-zinc-950/40 sticky bottom-0">
              <button
                onClick={() => setOpen(false)}
                className="w-full px-3 py-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors text-sm font-medium"
              >
                Done
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Component: Section wrapper
function Section({ title, children, hint }) {
  return (
    <div className="bg-white/70 dark:bg-zinc-900/70 backdrop-blur rounded-2xl shadow-lg p-4 md:p-6 border border-zinc-200 dark:border-zinc-800">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg md:text-xl font-semibold bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400 bg-clip-text text-transparent">
          {title}
        </h3>
        {hint && <span className="text-xs text-zinc-500">{hint}</span>}
      </div>
      {children}
    </div>
  );
}

// Component: Toolbar button
function ToolbarButton({ children, onClick, title, variant = "default" }) {
  const variants = {
    default: "border-zinc-200 dark:border-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800",
    primary: "bg-gradient-to-r from-blue-500 to-purple-500 text-white border-transparent hover:from-blue-600 hover:to-purple-600",
    danger: "border-red-300 dark:border-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400",
  };
  
  return (
    <button
      onClick={onClick}
      title={title}
      className={`px-3 py-2 rounded-xl border text-sm font-medium transition-all ${variants[variant]}`}
    >
      {children}
    </button>
  );
}

// Component: Combo dropdown with search
function ComboDropdown({ label, value, onChange, options, metaLabel = {} }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const btnRef = useRef(null);
  const popRef = useRef(null);

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") setOpen(false);
    }
    function onClick(e) {
      if (!open) return;
      if (
        popRef.current &&
        !popRef.current.contains(e.target) &&
        btnRef.current &&
        !btnRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    }
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  const items = options.filter(
    (o) => !q.trim() || o.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="relative">
      <div className="text-sm opacity-80 mb-1">{label}</div>
      <button
        ref={btnRef}
        onClick={() => setOpen((v) => !v)}
        className="w-full px-3 py-2 rounded-xl border border-zinc-200 dark:border-zinc-700 text-left flex items-center justify-between hover:bg-zinc-100/60 dark:hover:bg-zinc-800/60 transition-colors"
      >
        <span className="truncate">{value}</span>
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
        </svg>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => { setOpen(false); setQ(""); }} />
          <div
            ref={popRef}
            className="absolute z-50 mt-2 w-full bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-2xl shadow-2xl overflow-hidden"
            style={{ maxHeight: 'min(50vh, 320px)' }}
          >
          <div className="p-3 border-b border-zinc-200 dark:border-zinc-800 bg-zinc-50/70 dark:bg-zinc-950/40 sticky top-0">
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={`Search ${label.toLowerCase()}...`}
              className="w-full px-3 py-2 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900"
            />
          </div>
            <div className="overflow-y-auto p-2" style={{ maxHeight: 'min(40vh, 250px)' }}>
            {items.map((opt) => (
              <button
                key={opt}
                onClick={() => {
                  onChange(opt);
                  setOpen(false);
                  setQ("");
                }}
                className={`w-full text-left p-3 rounded-xl border ${
                  value === opt
                    ? "border-blue-400 dark:border-blue-600 bg-blue-50 dark:bg-blue-900/20"
                    : "border-zinc-200 dark:border-zinc-800"
                } hover:border-zinc-300 dark:hover:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 mb-2 transition-all`}
              >
                <div className="text-sm font-medium">{opt}</div>
                {metaLabel[opt] && (
                  <div className="text-xs text-zinc-500 mt-0.5">{metaLabel[opt]}</div>
                )}
              </button>
            ))}
            {items.length === 0 && (
              <div className="p-6 text-sm text-zinc-500 text-center">No match found.</div>
            )}
            </div>
          </div>
        </>
      )}
    </div>
  );

// Component: Template dropdown
function TemplateDropdown({ onSelect }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const btnRef = useRef(null);
  const popRef = useRef(null);
  const [dropdownAlign, setDropdownAlign] = useState('left');

  useEffect(() => {
    if (open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const spaceRight = window.innerWidth - rect.left;
      
      if (spaceRight < 450) {
        setDropdownAlign('right');
      } else {
        setDropdownAlign('left');
      }
    }
  }, [open]);

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") {
        setOpen(false);
        setQ("");
      }
    }
    function onClick(e) {
      if (!open) return;
      if (
        popRef.current &&
        !popRef.current.contains(e.target) &&
        btnRef.current &&
        !btnRef.current.contains(e.target)
      ) {
        setOpen(false);
        setQ("");
      }
    }
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  const items = TEMPLATES.map((t) => ({
    ...t,
    group: TEMPLATE_META[t.key]?.group || "Other",
    desc: TEMPLATE_META[t.key]?.desc || "",
  })).filter(
    (t) =>
      !q.trim() ||
      t.label.toLowerCase().includes(q.toLowerCase()) ||
      t.desc.toLowerCase().includes(q.toLowerCase())
  );
  
  const groups = Array.from(new Set(items.map((i) => i.group)));

  return (
    <div className="relative inline-block">
      <button
        ref={btnRef}
        onClick={() => setOpen((v) => !v)}
        className="px-3 py-2 rounded-xl border border-zinc-200 dark:border-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800 text-sm flex items-center gap-2 transition-colors"
      >
        <span>🧩 Templates</span>
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
        </svg>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => { setOpen(false); setQ(""); }} />
          <div
            ref={popRef}
            className={`absolute z-50 mt-2 ${dropdownAlign === 'right' ? 'right-0' : 'left-0'} w-[90vw] sm:w-[24rem] md:w-[28rem] bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-2xl shadow-2xl overflow-hidden`}
            style={{ maxWidth: 'min(28rem, 90vw)' }}
          >
          <div className="p-3 border-b border-zinc-200 dark:border-zinc-800 bg-zinc-50/70 dark:bg-zinc-950/40 sticky top-0">
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search templates..."
              className="w-full px-3 py-2 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900"
            />
            <div className="mt-2 text-[11px] text-zinc-500">
              Press <kbd className="px-1 py-0.5 rounded border">Esc</kbd> to close
            </div>
          </div>
            <div className="overflow-y-auto p-2" style={{ maxHeight: 'min(60vh, 400px)' }}>
            {groups.map((gr) => (
              <div key={gr} className="mb-3">
                <div className="px-2 py-1 text-xs font-semibold tracking-wide text-zinc-500 uppercase">
                  {gr}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {items
                    .filter((i) => i.group === gr)
                    .map((t) => (
                      <button
                        key={t.key}
                        onClick={() => {
                          onSelect(t.key);
                          setOpen(false);
                          setQ("");
                        }}
                        className="text-left p-3 rounded-xl border border-zinc-200 dark:border-zinc-800 hover:border-blue-300 dark:hover:border-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all"
                      >
                        <div className="text-sm font-medium">{t.label}</div>
                        {t.desc && (
                          <div className="text-xs text-zinc-500 mt-0.5 line-clamp-2">
                            {t.desc}
                          </div>
                        )}
                      </button>
                    ))}
                </div>
              </div>
            ))}
              {items.length === 0 && (
                <div className="p-6 text-sm text-zinc-500 text-center">
                  No templates match "{q}".
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
}

// Component: Preset bar
function PresetBar({
  loadPreset,
  applyTemplate,
  savePreset,
  presets,
  deletePreset,
  randomizeTone,
  randomizeStyle,
}) {
  const [name, setName] = useState("");
  
  return (
    <div className="flex flex-wrap items-center gap-2">
      <ToolbarButton title="Random tone" onClick={randomizeTone}>
        🎲 Tone
      </ToolbarButton>
      <ToolbarButton title="Random style" onClick={randomizeStyle}>
        🎲 Style
      </ToolbarButton>

      <div className="h-6 w-px bg-zinc-200 dark:bg-zinc-700 mx-1 hidden sm:block" />

      <TemplateDropdown onSelect={applyTemplate} />

      <div className="h-6 w-px bg-zinc-200 dark:bg-zinc-700 mx-1 hidden lg:block" />

      <div className="flex flex-wrap items-center gap-2">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Preset name"
          className="px-3 py-2 rounded-xl border bg-transparent border-zinc-200 dark:border-zinc-700 text-sm w-32 md:w-40"
        />
        <ToolbarButton
          title="Save preset"
          onClick={() => {
            if (name) {
              savePreset(name);
              setName("");
            }
          }}
        >
          💾 Save
        </ToolbarButton>
        <select
          className="px-3 py-2 rounded-xl border bg-transparent border-zinc-200 dark:border-zinc-700 text-sm"
          onChange={(e) => loadPreset(e.target.value)}
        >
          <option value="">Load preset...</option>
          {presets.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
        <ToolbarButton
          variant="danger"
          title="Delete preset"
          onClick={() => {
            const sel = prompt("Delete which preset? Type exact name:");
            if (sel) deletePreset(sel);
          }}
        >
          🗑️
        </ToolbarButton>
      </div>
    </div>
  );
}

// Build prompt function
function buildPrompt(s) {
  const parts = [];
  if (s.role) parts.push(`ROLE: ${s.role}`);
  if (s.task) parts.push(`OBJECTIVE: ${s.task}`);
  if (s.audience) parts.push(`AUDIENCE: ${s.audience}`);
  if (s.context) parts.push(`CONTEXT:\n${s.context}`);
  
  // Add Framework if selected
  if (s.framework) {
    const framework = FRAMEWORKS.find(f => f.key === s.framework);
    if (framework) {
      parts.push(`FRAMEWORK: Apply the ${framework.label} framework (${framework.desc})`);
    }
  }
  
  // Add SEO Optimizations if selected
  if (s.optimizations && s.optimizations.length > 0) {
    const optList = s.optimizations.map(key => {
      const opt = SEO_OPTIMIZATIONS.find(o => o.key === key);
      return opt ? `- ${opt.label}: ${opt.desc}` : null;
    }).filter(Boolean);
    if (optList.length > 0) {
      parts.push(`SEO OPTIMIZATIONS:\n${optList.join("\n")}`);
    }
  }

  const rules = [];
  if (s.tone) rules.push(`Tone: ${s.tone}`);
  if (s.style) rules.push(`Style: ${s.style}`);
  if (s.language && s.language !== "Auto") rules.push(`Language: ${s.language}`);
  if (s.length) rules.push(`Target length: ~${s.length} words`);
  if (s.depth) rules.push(`Depth: ${s.depth}/5`);
  if (s.output) rules.push(`Output format: ${s.output}`);

  const constraints = (s.constraints || []).filter(Boolean);
  if (constraints.length) {
    rules.push(`Constraints:\n${constraints.map((c, i) => `${i + 1}. ${c}`).join("\n")}`);
  }

  if (rules.length) parts.push(`GUIDELINES:\n${rules.join("\n")}`);

  parts.push(
    `DELIVERABLE:\n- Provide only the requested output format.\n- If information is missing, state assumptions briefly then proceed.`
  );

  return parts.join("\n\n").trim() + "\n";
}

// Main App Component
export default function PromptGeneratorApp() {
  const [state, setState] = useLocalState(DEFAULT_STATE);
  const [darkMode, setDarkMode] = useState(false);

  useEffect(() => {
    // Check for system dark mode preference
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      setDarkMode(true);
    }
  }, []);

  useEffect(() => {
    // Apply dark mode class to HTML element
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  const set = (k, v) => setState((s) => ({ ...s, [k]: v }));
  
  const updateConstraint = (i, v) =>
    setState((s) => {
      const constraints = s.constraints.slice();
      constraints[i] = v;
      return { ...s, constraints };
    });
    
  const addConstraint = () =>
    setState((s) => ({ ...s, constraints: [...s.constraints, ""] }));
    
  const removeConstraint = (i) =>
    setState((s) => ({
      ...s,
      constraints: s.constraints.filter((_, idx) => idx !== i),
    }));

  const presets = useMemo(
    () => Object.keys(JSON.parse(localStorage.getItem("promptgen/presets") || "{}")),
    [state]
  );
  
  const savePreset = (name) => {
    const all = JSON.parse(localStorage.getItem("promptgen/presets") || "{}");
    all[name] = state;
    localStorage.setItem("promptgen/presets", JSON.stringify(all));
    alert(`Preset "${name}" saved!`);
  };
  
  const loadPreset = (name) => {
    if (!name) return;
    const all = JSON.parse(localStorage.getItem("promptgen/presets") || "{}");
    if (all[name]) setState(all[name]);
  };
  
  const deletePreset = (name) => {
    const all = JSON.parse(localStorage.getItem("promptgen/presets") || "{}");
    delete all[name];
    localStorage.setItem("promptgen/presets", JSON.stringify(all));
    setState((s) => ({ ...s }));
    alert(`Preset "${name}" deleted!`);
  };

  const randomizeTone = () =>
    set("tone", TONES[Math.floor(Math.random() * TONES.length)]);
    
  const randomizeStyle = () =>
    set("style", STYLES[Math.floor(Math.random() * STYLES.length)]);

  const promptText = useMemo(() => buildPrompt(state), [state]);
  const { words, tokens } = useMemo(() => estimateTokens(promptText), [promptText]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(promptText);
      alert("Copied to clipboard!");
    } catch (e) {
      alert("Copy failed: " + e.message);
    }
  };
  
  const downloadTxt = () => {
    const blob = new Blob([promptText], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `prompt-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };
  
  const newDoc = () => {
    if (confirm("Reset all fields to default? This cannot be undone.")) {
      setState(DEFAULT_STATE);
    }
  };

  const applyTemplate = (key) => {
    const tpl = TEMPLATES.find((t) => t.key === key);
    if (!tpl) return;
    setState((s) => ({ 
      ...s, 
      ...tpl.preset, 
      templateKey: key,
      templateType: tpl.type || "",
      framework: "",
      optimizations: []
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-50 via-white to-zinc-100 dark:from-zinc-950 dark:via-black dark:to-zinc-900 text-zinc-900 dark:text-zinc-100">
      {/* Header */}
      <header className="sticky top-0 z-30 backdrop-blur-md supports-[backdrop-filter]:bg-white/60 dark:supports-[backdrop-filter]:bg-black/40 border-b border-zinc-200 dark:border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 sm:gap-4">
                <h1 className="text-lg sm:text-xl md:text-2xl font-bold tracking-tight bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400 bg-clip-text text-transparent">
                  AI Prompt Generator
                </h1>
                <span className="px-2 py-1 text-xs font-medium bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-full">
                  v2.0
                </span>
              </div>
              <button
                onClick={() => setDarkMode(!darkMode)}
                className="p-2 rounded-xl border border-zinc-200 dark:border-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors sm:ml-auto"
                title="Toggle dark mode"
              >
                {darkMode ? "🌞" : "🌙"}
              </button>
            </div>
            <div className="w-full">
            <PresetBar
              presets={presets}
              savePreset={savePreset}
              loadPreset={loadPreset}
              deletePreset={deletePreset}
              applyTemplate={applyTemplate}
              randomizeTone={randomizeTone}
              randomizeStyle={randomizeStyle}
            />
          </div>
        </div>
      </div>
    </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6 grid lg:grid-cols-2 gap-6">
        {/* Left Column - Input */}
        <div className="space-y-6">
          <Section title="1) Objective & Role" hint="Define what you want">
            <div className="grid gap-4">
              <div>
                <label className="text-sm opacity-80">Task / Objective</label>
                <textarea
                  value={state.task}
                  onChange={(e) => set("task", e.target.value)}
                  placeholder="e.g., Write an SEO-optimized blog post about sustainable fashion..."
                  className="mt-1 w-full px-3 py-2 rounded-xl border bg-transparent border-zinc-200 dark:border-zinc-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  rows={2}
                />
              </div>
              <div>
                <label className="text-sm opacity-80">Role / Persona</label>
                <input
                  value={state.role}
                  onChange={(e) => set("role", e.target.value)}
                  placeholder="e.g., You are an expert content strategist and SEO specialist"
                  className="mt-1 w-full px-3 py-2 rounded-xl border bg-transparent border-zinc-200 dark:border-zinc-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
              </div>
              <div>
                <label className="text-sm opacity-80">Target Audience</label>
                <input
                  value={state.audience}
                  onChange={(e) => set("audience", e.target.value)}
                  placeholder="e.g., Environmentally conscious millennials"
                  className="mt-1 w-full px-3 py-2 rounded-xl border bg-transparent border-zinc-200 dark:border-zinc-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
              </div>
            </div>
          </Section>

          <Section title="2) Context & Constraints" hint="Background and rules">
            <div className="grid gap-4">
              <div>
                <label className="text-sm opacity-80">Context / Background Info</label>
                <textarea
                  value={state.context}
                  onChange={(e) => set("context", e.target.value)}
                  rows={4}
                  placeholder="Paste relevant information, data, examples, or requirements here..."
                  className="mt-1 w-full px-3 py-2 rounded-xl border bg-transparent border-zinc-200 dark:border-zinc-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
              </div>

              <div>
                <label className="text-sm opacity-80">
                  Constraints / Requirements
                </label>
                <div className="mt-2 space-y-2">
                  {state.constraints.map((c, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="text-sm font-medium text-zinc-500 w-6">
                        {i + 1}.
                      </span>
                      <input
                        value={c}
                        onChange={(e) => updateConstraint(i, e.target.value)}
                        placeholder={`Add constraint #${i + 1}`}
                        className="flex-1 px-3 py-2 rounded-xl border bg-transparent border-zinc-200 dark:border-zinc-700 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      />
                      <button
                        onClick={() => removeConstraint(i)}
                        className="p-2 text-zinc-500 hover:text-red-500 transition-colors"
                        title="Remove"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={addConstraint}
                    className="px-4 py-2 rounded-xl border border-dashed hover:border-solid border-zinc-300 dark:border-zinc-700 text-sm transition-all"
                  >
                    + Add Constraint
                  </button>
                </div>
              </div>
            </div>
          </Section>

          <Section title="3) Framework & Optimizations" hint="Enhance your content">
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <FrameworkSelector
                value={state.framework}
                onChange={(v) => set("framework", v)}
              />
              <OptimizationsSelector
                value={state.optimizations}
                onChange={(v) => set("optimizations", v)}
              />
            </div>
            {state.framework && (
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800 mb-4">
                <div className="text-sm font-medium text-blue-900 dark:text-blue-100">
                  Framework Applied: {FRAMEWORKS.find(f => f.key === state.framework)?.label}
                </div>
                <div className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                  {FRAMEWORKS.find(f => f.key === state.framework)?.desc}
                </div>
              </div>
            )}
            {state.optimizations.length > 0 && (
              <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800 mb-4">
                <div className="text-sm font-medium text-green-900 dark:text-green-100 mb-2">
                  SEO Optimizations Active:
                </div>
                <div className="flex flex-wrap gap-2">
                  {state.optimizations.map(opt => (
                    <span key={opt} className="px-2 py-1 bg-green-100 dark:bg-green-800/30 rounded-lg text-xs">
                      {SEO_OPTIMIZATIONS.find(o => o.key === opt)?.label}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </Section>

          <Section title="4) Style & Format" hint="Fine-tune the output">
            <div className="grid md:grid-cols-2 gap-4">
              <ComboDropdown
                label="Tone"
                value={state.tone}
                onChange={(v) => set("tone", v)}
                options={TONES}
                metaLabel={TONE_META}
              />
              <ComboDropdown
                label="Writing Style"
                value={state.style}
                onChange={(v) => set("style", v)}
                options={STYLES}
                metaLabel={STYLE_META}
              />
              <div>
                <label className="text-sm opacity-80">Language</label>
                <select
                  value={state.language}
                  onChange={(e) => set("language", e.target.value)}
                  className="mt-1 w-full px-3 py-2 rounded-xl border bg-transparent border-zinc-200 dark:border-zinc-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                >
                  {LANGS.map((l) => (
                    <option key={l} value={l}>
                      {l}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm opacity-80">Length (words)</label>
                <input
                  type="number"
                  min={50}
                  step={50}
                  value={state.length}
                  onChange={(e) =>
                    set("length", Math.max(0, Number(e.target.value || 0)))
                  }
                  className="mt-1 w-full px-3 py-2 rounded-xl border bg-transparent border-zinc-200 dark:border-zinc-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-sm opacity-80">
                  Detail Level: {state.depth}/5
                </label>
                <input
                  type="range"
                  min={1}
                  max={5}
                  value={state.depth}
                  onChange={(e) => set("depth", Number(e.target.value))}
                  className="mt-2 w-full accent-blue-500"
                />
                <div className="flex justify-between text-xs text-zinc-500 mt-1">
                  <span>Surface</span>
                  <span>Moderate</span>
                  <span>Exhaustive</span>
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="text-sm opacity-80">Output Format</label>
                <input
                  value={state.output}
                  onChange={(e) => set("output", e.target.value)}
                  placeholder="e.g., Markdown article, JSON, Email, Bullet points"
                  className="mt-1 w-full px-3 py-2 rounded-xl border bg-transparent border-zinc-200 dark:border-zinc-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
              </div>
            </div>
          </Section>

          <div className="flex flex-wrap gap-2">
            <ToolbarButton onClick={copy} variant="primary">
              📋 Copy Prompt
            </ToolbarButton>
            <ToolbarButton onClick={downloadTxt}>⬇️ Download</ToolbarButton>
            <ToolbarButton onClick={newDoc} variant="danger">
              🔄 Reset All
            </ToolbarButton>
            <div className="flex-1" />
            <ToolbarButton
              onClick={() => applyTemplate("blog_seo_onpage")}
              title="Quick: Blog SEO Onpage"
            >
              ⚡ SEO Blog
            </ToolbarButton>
            <ToolbarButton
              onClick={() => applyTemplate("news_post_authority")}
              title="Quick: News Post Authority"
            >
              ⚡ News Post
            </ToolbarButton>
          </div>
        </div>

        {/* Right Column - Preview */}
        <div className="space-y-6">
          <Section title="Generated Prompt" hint="Copy this to your AI">
            <div className="relative">
              <textarea
                readOnly
                value={promptText}
                rows={20}
                className="w-full font-mono text-sm leading-relaxed whitespace-pre-wrap rounded-xl border bg-zinc-50 dark:bg-zinc-950 border-zinc-200 dark:border-zinc-800 p-4 resize-none"
              />
              <div className="absolute top-2 right-2 flex gap-2">
                <button
                  onClick={copy}
                  className="p-2 bg-white dark:bg-zinc-900 rounded-lg shadow-md hover:shadow-lg transition-shadow"
                  title="Copy to clipboard"
                >
                  📋
                </button>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-zinc-600 dark:text-zinc-400">Words:</span>
                <span className="font-semibold text-blue-600 dark:text-blue-400">
                  {words}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-zinc-600 dark:text-zinc-400">Tokens:</span>
                <span className="font-semibold text-purple-600 dark:text-purple-400">
                  ~{tokens}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-zinc-600 dark:text-zinc-400">Depth:</span>
                <span className="font-semibold">{state.depth}/5</span>
              </div>
            </div>
          </Section>

          <Section title="Quick Tips" hint="Get better results">
            <ul className="space-y-2 text-sm">
              <li className="flex items-start gap-2">
                <span className="text-blue-500 mt-0.5">•</span>
                <span>
                  Replace placeholder text in &lt;angle brackets&gt; with your actual content
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-500 mt-0.5">•</span>
                <span>
                  Be specific with your role and objective for best results
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-500 mt-0.5">•</span>
                <span>
                  Add constraints to control the output format and requirements
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-500 mt-0.5">•</span>
                <span>
                  Use templates as starting points and customize them
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-500 mt-0.5">•</span>
                <span>
                  Save frequently used prompts as presets for quick access
                </span>
              </li>
            </ul>
          </Section>

          <Section title="Template Categories" hint="Explore frameworks">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
              {Object.entries(
                TEMPLATES.reduce((acc, t) => {
                  const group = TEMPLATE_META[t.key]?.group || "Other";
                  acc[group] = (acc[group] || 0) + 1;
                  return acc;
                }, {})
              ).map(([group, count]) => (
                <div
                  key={group}
                  className="px-3 py-2 rounded-lg bg-zinc-100 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700"
                >
                  <div className="font-medium">{group}</div>
                  <div className="text-xs text-zinc-500">{count} templates</div>
                </div>
              ))}
            </div>
          </Section>
        </div>
      </main>

      {/* Footer */}
      <footer className="mt-12 border-t border-zinc-200 dark:border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="text-center space-y-2">
            <p className="text-sm text-zinc-600 dark:text-zinc-400">
              Built with React & Tailwind CSS • Works offline • Data saved locally
            </p>
            <p className="text-xs text-zinc-500">
              © 2024 AI Prompt Generator • Share your prompts with the URL hash
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}